// ~/util/bibliotecaDeFunciones.js
export default class BibliotecaDeFunciones {
  /**
   * Devuelve todas las funciones disponibles en la biblioteca.
   * Esta es la que usarás normalmente en el editor.
   */
  static obtenerFunciones() {
    return [
      this._definirImprimePosicion(),
      this._definirFechaCalcula(),
      this._definirConcatena(),
      this._definirTextoMayusculas(),
      this._definirTextoMinusculas(),
      this._definirValorPorDefecto(),
      this._definirReemplazaTexto(),
      this._definirRedondeaNumero(),
      this._definirFormateaNumero(),
      this._definirCalculaEdad(),
    ]
  }

  /**
   * Devuelve la definición de una función concreta por su nombre,
   * por ejemplo: "imprimePosicion", "fechaCalcula", etc.
   */
  static obtenerFuncionPorNombre(nombreFuncion) {
    return (
      this.obtenerFunciones().find((definicion) => definicion.nombreFuncion === nombreFuncion) ||
      null
    )
  }

  /**
   * Texto de ayuda general sobre la sintaxis y el uso de funciones/variables.
   * Ideal para mostrar en un dialog de "Ayuda".
   */
  static obtenerAyudaSintaxisGeneral() {
    return {
      titulo: 'Ayuda de sintaxis de variables y funciones',
      descripcion:
        'Esta sección explica cómo escribir variables, alias y funciones dentro de las plantillas usando el formato ${...}.',
      secciones: [
        {
          titulo: '1. Marcadores básicos',
          contenido: [
            'Todas las expresiones se escriben entre ${ y }.',
            'Ejemplo simple: ${v:solicitud.user.nombre}',
            'Dentro de ${...} puedes usar variables, alias y funciones.',
          ],
        },
        {
          titulo: '2. Acceso a datos (prefijos de variables)',
          contenido: [
            'v:ruta       → Inserta un valor de texto usando la ruta completa (fullPath).',
            'i:ruta       → Inserta una imagen base64 usando la ruta completa (fullPath).',
            'va:alias     → Inserta un valor de texto usando el alias definido en el editor.',
            'ia:alias     → Inserta una imagen base64 usando el alias definido en el editor.',
          ],
          ejemplos: [
            '${v:solicitud.user.nombre}',
            '${va:nombreOperador}',
            '${i:solicitud.user.empresa.logotipo}',
            '${ia:logotipoEmpresa}',
          ],
        },
        {
          titulo: '3. Funciones',
          contenido: [
            'Las funciones se escriben como: ${nombreFuncion:arg1,arg2,...}',
            'Cada argumento puede ser:',
            '- Un literal de texto entre comillas: "Hola mundo"',
            '- Un número: 0, 3, 25.5',
            '- Una ruta/alias: v:solicitud.user.nombre, va:nombreOperador',
          ],
          ejemplos: [
            '${textoMayusculas:v:solicitud.user.nombre}',
            '${imprimePosicion:va:nombreOperador,0,3}',
            '${concatena:v:solicitud.user.nombre," ",v:solicitud.user.apellido1}',
          ],
        },
        {
          titulo: '4. Combinación de funciones',
          contenido: [
            'Algunas funciones admiten como argumento el resultado de otra función.',
            'Sintaxis recomendada: ${funcionExterna:{funcionInterna:...}}',
            'De esta forma la función interna se resuelve primero.',
          ],
          ejemplos: ['${textoMayusculas:{imprimePosicion:va:nombreOperador,0,3}}'],
        },
        {
          titulo: '5. Alias',
          contenido: [
            'Los alias se definen en el editor de variables.',
            'Es recomendable usar alias (va:/ia:) en lugar de rutas largas, sobre todo si la ruta contiene índices (0,1,2,...) o es muy verbosa.',
            'Si cambia la estructura interna del JSON, solo tendrás que actualizar el alias.',
          ],
          ejemplos: [
            'En lugar de ${v:solicitud.productos.0.params.0.valor.0}',
            'puedes definir un alias "primerProducto" y usar ${va:primerProducto}.',
          ],
        },
        {
          titulo: '6. Estados de las funciones',
          contenido: [
            'Cada función de la biblioteca tiene un estado:',
            '- "lista"         → la función está implementada y se puede usar en plantillas.',
            '- "en_desarrollo" → está definida pero aún puede cambiar su comportamiento.',
            '- "pendiente"     → está planificada pero todavía no está disponible.',
            'Desde el editor puedes ver este estado para saber qué funciones están listas para usar.',
          ],
        },
      ],
    }
  }

  // ---------------------------------------------------------------------------
  // Definiciones individuales (privadas) de cada función
  // ---------------------------------------------------------------------------

  static _definirImprimePosicion() {
    return {
      nombreFuncion: 'imprimePosicion',
      etiqueta: 'Imprime posición',
      categoria: 'Texto',
      estado: 'pendiente', // 'lista' | 'en_desarrollo' | 'pendiente'

      descripcion:
        'Devuelve un fragmento del texto de origen, indicando la posición inicial y la longitud a extraer. Útil para crear códigos o iniciales a partir de nombres o identificadores más largos.',
      sintaxis: '${imprimePosicion:origen,posicionInicial,longitud}',
      plantillaExpresion: '${imprimePosicion:CAMPO,0,1}',
      ejemplo: '${imprimePosicion:nodo.apellidoPaterno,0,3}  // "CAR" si el apellido es "Cardenas"',

      argumentos: [
        {
          nombre: 'origen',
          etiqueta: 'Texto de origen',
          tipo: 'string|path',
          requerido: true,
          descripcion:
            'Texto, variable o ruta JSON desde la que se extrae el fragmento. Puede ser v:ruta o va:alias.',
          ejemplo: 'nodo.apellidoPaterno',
        },
        {
          nombre: 'posicionInicial',
          etiqueta: 'Posición inicial',
          tipo: 'number',
          requerido: true,
          descripcion: 'Índice inicial (basado en 0) desde el que se empieza a extraer el texto.',
          ejemplo: '0',
        },
        {
          nombre: 'longitud',
          etiqueta: 'Longitud',
          tipo: 'number',
          requerido: true,
          descripcion: 'Número de caracteres a extraer a partir de la posición inicial.',
          ejemplo: '3',
        },
      ],

      tipoRetorno: 'string',
    }
  }

  static _definirFechaCalcula() {
    return {
      nombreFuncion: 'fechaCalcula',
      etiqueta: 'Fecha calcula / formatea',
      categoria: 'Fecha y hora',
      estado: 'pendiente',

      descripcion:
        'Trabaja con fechas: permite formatear una fecha de origen y devolver solo partes específicas (año, mes, día) o un formato completo legible.',
      sintaxis: '${fechaCalcula:fechaOrigen,"patronSalida"}',
      plantillaExpresion: '${fechaCalcula:CAMPO,"yyyy"}',
      ejemplo: '${fechaCalcula:nodo.fechaNacimiento,"yyyy"}  // "1989" si la fecha es 1989-10-03',

      argumentos: [
        {
          nombre: 'fechaOrigen',
          etiqueta: 'Fecha origen',
          tipo: 'date|string|path',
          requerido: true,
          descripcion:
            'Fecha de origen. Puede ser un string con formato de fecha o una ruta JSON hacia una fecha.',
          ejemplo: 'nodo.fechaNacimiento',
        },
        {
          nombre: 'patronSalida',
          etiqueta: 'Patrón salida',
          tipo: 'string',
          requerido: true,
          descripcion:
            'Patrón de formato de fecha (por ejemplo "yyyy", "dd/MM/yyyy", "MMMM yyyy", etc.).',
          ejemplo: '"dd/MM/yyyy"',
        },
      ],

      tipoRetorno: 'string',
    }
  }

  static _definirConcatena() {
    return {
      nombreFuncion: 'concatena',
      etiqueta: 'Concatena textos',
      categoria: 'Texto',
      estado: 'pendiente',

      descripcion:
        'Concatena varios textos, variables o valores literales en un único resultado. Ideal para construir frases, códigos o descripciones a partir de varios campos.',
      sintaxis: '${concatena:valor1," ",valor2,...}',
      plantillaExpresion: '${concatena:CAMPO1," ",CAMPO2}',
      ejemplo: '${concatena:nodo.nombre," ",nodo.apellidoPaterno}  // "Jorge Cardenas"', // eslint-disable-line

      argumentos: [
        {
          nombre: 'valores',
          etiqueta: 'Valores a concatenar',
          tipo: 'variadic',
          requerido: true,
          descripcion:
            'Lista de textos, variables o literales que se unirán en una sola cadena. Se separan por comas.',
          ejemplo: 'nodo.nombre," ",nodo.apellidoPaterno',
        },
      ],

      tipoRetorno: 'string',
    }
  }

  static _definirTextoMayusculas() {
    return {
      nombreFuncion: 'textoMayusculas',
      etiqueta: 'Texto en mayúsculas',
      categoria: 'Texto',
      estado: 'pendiente',

      descripcion:
        'Convierte el texto de origen completamente a mayúsculas. Útil para códigos, etiquetas o textos destacados.',
      sintaxis: '${textoMayusculas:origen}',
      plantillaExpresion: '${textoMayusculas:CAMPO}',
      ejemplo: '${textoMayusculas:nodo.nombre}  // "JORGE" si el nombre es "Jorge"',

      argumentos: [
        {
          nombre: 'origen',
          etiqueta: 'Texto de origen',
          tipo: 'string|path',
          requerido: true,
          descripcion:
            'Texto o variable que se desea transformar a mayúsculas (v:ruta o va:alias).',
          ejemplo: 'nodo.nombre',
        },
      ],

      tipoRetorno: 'string',
    }
  }

  static _definirTextoMinusculas() {
    return {
      nombreFuncion: 'textoMinusculas',
      etiqueta: 'Texto en minúsculas',
      categoria: 'Texto',
      estado: 'pendiente',

      descripcion:
        'Convierte el texto de origen completamente a minúsculas. Útil para normalizar datos o comparaciones.',
      sintaxis: '${textoMinusculas:origen}',
      plantillaExpresion: '${textoMinusculas:CAMPO}',
      ejemplo: '${textoMinusculas:nodo.nombre}  // "jorge" si el nombre es "Jorge"',

      argumentos: [
        {
          nombre: 'origen',
          etiqueta: 'Texto de origen',
          tipo: 'string|path',
          requerido: true,
          descripcion:
            'Texto o variable que se desea transformar a minúsculas (v:ruta o va:alias).',
          ejemplo: 'nodo.nombre',
        },
      ],

      tipoRetorno: 'string',
    }
  }

  static _definirValorPorDefecto() {
    return {
      nombreFuncion: 'valorPorDefecto',
      etiqueta: 'Valor por defecto',
      categoria: 'Utilidades',
      estado: 'pendiente',

      descripcion:
        'Devuelve el valor de origen si existe y es válido; en caso contrario, devuelve un valor por defecto definido por el usuario.',
      sintaxis: '${valorPorDefecto:origen,valorDefecto}',
      plantillaExpresion: '${valorPorDefecto:CAMPO,"N/D"}',
      ejemplo: '${valorPorDefecto:nodo.telefono,"Sin teléfono"}',

      argumentos: [
        {
          nombre: 'origen',
          etiqueta: 'Valor origen',
          tipo: 'any|path',
          requerido: true,
          descripcion:
            'Valor o ruta JSON que se desea utilizar si está presente (no nulo, no vacío, no undefined).',
          ejemplo: 'nodo.telefono',
        },
        {
          nombre: 'valorDefecto',
          etiqueta: 'Valor por defecto',
          tipo: 'any',
          requerido: true,
          descripcion:
            'Valor alternativo a devolver cuando el origen sea nulo, vacío o no definido.',
          ejemplo: '"Sin teléfono"',
        },
      ],

      tipoRetorno: 'any',
    }
  }

  static _definirReemplazaTexto() {
    return {
      nombreFuncion: 'reemplazaTexto',
      etiqueta: 'Reemplaza texto',
      categoria: 'Texto',
      estado: 'pendiente',

      descripcion:
        'Reemplaza todas las apariciones de un texto dentro del texto de origen por otro valor. Muy útil para limpiar datos, cambiar separadores o adaptar textos al formato de la plantilla.',
      sintaxis: '${reemplazaTexto:origen,"buscar","reemplazo"}',
      plantillaExpresion: '${reemplazaTexto:CAMPO," ","_"}',
      ejemplo: '${reemplazaTexto:nodo.nombreCompleto," ","_"}  // "Jorge_Cardenas_Valencia"', // eslint-disable-line

      argumentos: [
        {
          nombre: 'origen',
          etiqueta: 'Texto de origen',
          tipo: 'string|path',
          requerido: true,
          descripcion:
            'Texto o variable sobre el que se quiere realizar el reemplazo (v:ruta o va:alias).',
          ejemplo: 'nodo.nombreCompleto',
        },
        {
          nombre: 'buscar',
          etiqueta: 'Texto a buscar',
          tipo: 'string',
          requerido: true,
          descripcion: 'Texto literal que se desea sustituir dentro del origen.',
          ejemplo: '" "',
        },
        {
          nombre: 'reemplazo',
          etiqueta: 'Texto de reemplazo',
          tipo: 'string',
          requerido: true,
          descripcion: 'Texto que sustituye a cada aparición del texto indicado en "buscar".',
          ejemplo: '"_"',
        },
      ],

      tipoRetorno: 'string',
    }
  }

  static _definirRedondeaNumero() {
    return {
      nombreFuncion: 'redondeaNumero',
      etiqueta: 'Redondea número',
      categoria: 'Números',
      estado: 'pendiente',

      descripcion:
        'Redondea un número al número de decimales indicado. Útil para informes económicos, cantidades, porcentajes, etc.',
      sintaxis: '${redondeaNumero:valor,decimales}',
      plantillaExpresion: '${redondeaNumero:CAMPO,2}',
      ejemplo: '${redondeaNumero:nodo.importe,2}  // 1234.57 si el importe es 1234.567',

      argumentos: [
        {
          nombre: 'valor',
          etiqueta: 'Valor numérico',
          tipo: 'number|path',
          requerido: true,
          descripcion:
            'Número o ruta JSON que indica el valor a redondear. Si no es numérico, la función puede devolver vacío o error según implementación.',
          ejemplo: 'nodo.importe',
        },
        {
          nombre: 'decimales',
          etiqueta: 'Número de decimales',
          tipo: 'number',
          requerido: true,
          descripcion: 'Cantidad de decimales a dejar en el resultado.',
          ejemplo: '2',
        },
      ],

      tipoRetorno: 'number',
    }
  }

  static _definirFormateaNumero() {
    return {
      nombreFuncion: 'formateaNumero',
      etiqueta: 'Formatea número',
      categoria: 'Números',
      estado: 'pendiente',

      descripcion:
        'Formatea un número para mostrarlo con separadores de miles y decimales configurables. Pensado para informes donde la presentación del número es importante.',
      sintaxis: '${formateaNumero:valor,decimales,"separadorDecimal","separadorMiles"}',
      plantillaExpresion: '${formateaNumero:CAMPO,2,",","."}',
      ejemplo: '${formateaNumero:nodo.importe,2,",","."}  // "1.234,57" para 1234.567', // eslint-disable-line

      argumentos: [
        {
          nombre: 'valor',
          etiqueta: 'Valor numérico',
          tipo: 'number|path',
          requerido: true,
          descripcion: 'Número o ruta JSON del valor a formatear. Debe ser un valor numérico.',
          ejemplo: 'nodo.importe',
        },
        {
          nombre: 'decimales',
          etiqueta: 'Número de decimales',
          tipo: 'number',
          requerido: true,
          descripcion:
            'Cantidad de decimales en el resultado formateado (por ejemplo 2 para céntimos).',
          ejemplo: '2',
        },
        {
          nombre: 'separadorDecimal',
          etiqueta: 'Separador decimal',
          tipo: 'string',
          requerido: true,
          descripcion:
            'Carácter usado para separar decimales (por ejemplo "," en España o "." en formato internacional).',
          ejemplo: '","',
        },
        {
          nombre: 'separadorMiles',
          etiqueta: 'Separador de miles',
          tipo: 'string',
          requerido: true,
          descripcion:
            'Carácter usado para separar miles (por ejemplo "." en España o "," en formato internacional).',
          ejemplo: '"."',
        },
      ],

      tipoRetorno: 'string',
    }
  }

  static _definirCalculaEdad() {
    return {
      nombreFuncion: 'calculaEdad',
      etiqueta: 'Calcula edad',
      categoria: 'Fecha y hora',
      estado: 'pendiente',

      descripcion:
        'Calcula la edad en años a partir de una fecha de nacimiento. Muy útil para informes de personas, empleados, alumnos, etc.',
      sintaxis: '${calculaEdad:fechaNacimiento}',
      plantillaExpresion: '${calculaEdad:CAMPO}',
      ejemplo: '${calculaEdad:nodo.fechaNacimiento}  // 36',

      argumentos: [
        {
          nombre: 'fechaNacimiento',
          etiqueta: 'Fecha de nacimiento',
          tipo: 'date|string|path',
          requerido: true,
          descripcion:
            'Fecha de nacimiento a partir de la cual se calculará la edad actual. Puede ser un string o una ruta JSON.',
          ejemplo: 'nodo.fechaNacimiento',
        },
      ],

      tipoRetorno: 'number',
    }
  }
}
